import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CommonButton } from "./Components/common/commonButton";
import { logout, setIsSessionExpired } from "./redux/slice/loginSlice";

export default function TimeoutProvider({ children }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const { isLoggedIn } = useSelector((state) => state?.login);
  const location = useLocation();
  const dispatch = useDispatch();

  const sessionTimeOutRef = useRef();
  const sessionWarningTimeOutRef = useRef();
  const countdownIntervalRef = useRef();
  const isDialogOpenRef = useRef(false);
  const expiryAtRef = useRef(0);
  const warningAtRef = useRef(0);

  const sessionTimeOutTime = Number(process.env.REACT_APP_IDLE_SESSION_TIMEOUT); // e.g., 600000
  const sessionWarningTime = Number(process.env.REACT_APP_IDLE_SESSION_WARNING_TIMEOUT); // e.g., 540000

  const eventsList = [
    "mousedown",
    "mousemove",
    "click",
    "wheel",
    "keydown",
    "touchstart",
    "scroll",
  ];

  const exemptedRoutes = ["/", "/login", "/forget-password"];

  const broadcastActivity = () => {
    localStorage.setItem("user-activity", Date.now().toString());
  };

  const broadcastLogout = () => {
    localStorage.setItem("force-logout", Date.now().toString());
  };

  const clearSessionTimers = () => {
    clearTimeout(sessionTimeOutRef.current);
    clearTimeout(sessionWarningTimeOutRef.current);
    clearInterval(countdownIntervalRef.current);
  };

  const handleManualLogout = () => {
    setIsDialogOpen(false);
    clearSessionTimers();
    broadcastLogout();
    dispatch(logout());
  };

  const handleSessionTimeout = () => {
    setIsDialogOpen(false);
    clearSessionTimers();
    broadcastLogout();
    dispatch(setIsSessionExpired(true));
  };

  const handleStay = () => {
    setIsDialogOpen(false);
    handleWindowEvents();
  };

  const handleWindowEvents = () => {
    if (!isLoggedIn) return;
    if (isDialogOpenRef.current) return;

    clearSessionTimers();
    expiryAtRef.current = Date.now() + sessionTimeOutTime;
    warningAtRef.current = Date.now() + sessionWarningTime;
    setRemainingTime(Math.max(expiryAtRef.current - Date.now(), 0));

    // Auto logout timer
    sessionTimeOutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, Math.max(expiryAtRef.current - Date.now(), 0));

    // Warning dialog timer
    sessionWarningTimeOutRef.current = setTimeout(() => {
      setIsDialogOpen(true);
    }, Math.max(warningAtRef.current - Date.now(), 0));

    // Countdown timer
    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(expiryAtRef.current - Date.now(), 0);
      setRemainingTime(remaining);
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 1000);

    broadcastActivity();
  };

  const handleStorageChange = (e) => {
    if (e.key === "force-logout") {
      setIsDialogOpen(false);
      clearSessionTimers();
      dispatch(logout());
    }
    if (e.key === "user-activity") {
      handleWindowEvents();
    }
  };

  // Keep ref in sync with latest dialog state for event handlers
  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen) {
      // Stop countdown while dialog is open
      clearInterval(countdownIntervalRef.current);
    }
  }, [isDialogOpen]);

  // Keep remaining time accurate when tab visibility/focus changes
  useEffect(() => {
    const syncRemaining = () => {
      if (!isLoggedIn) return;
      if (isDialogOpenRef.current) return;
      setRemainingTime(Math.max(expiryAtRef.current - Date.now(), 0));
    };

    window.addEventListener("focus", syncRemaining, { passive: true });
    document.addEventListener("visibilitychange", syncRemaining);

    return () => {
      window.removeEventListener("focus", syncRemaining, { passive: true });
      document.removeEventListener("visibilitychange", syncRemaining);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (exemptedRoutes.includes(location.pathname)) return;

    handleWindowEvents();

    eventsList.forEach((event) =>
      window.addEventListener(event, handleWindowEvents, { passive: true })
    );

    window.addEventListener("storage", handleStorageChange);

    return () => {
      eventsList.forEach((event) =>
        window.removeEventListener(event, handleWindowEvents, { passive: true })
      );
      window.removeEventListener("storage", handleStorageChange);
      clearSessionTimers();
    };
  }, [location.pathname, isLoggedIn]);

  return (
    <>
      {children}

      {isLoggedIn && (
        <div
          style={{
            position: "fixed",
            bottom: 10,
            right: 10,
            backgroundColor: "#e3f2fd",
            padding: "8px 12px",
            border: "1px solid #90caf9",
            borderRadius: 4,
            color: "#1565c0",
            fontWeight: "bold",
            fontSize: "14px",
            zIndex: 9999,
          }}
        >
          Session expires in:{" "}
          {Math.floor(remainingTime / 60000)}m{" "}
          {Math.floor((remainingTime % 60000) / 1000)}s
        </div>
      )}

      {isDialogOpen && isLoggedIn && (
        <Dialog open={isDialogOpen} fullWidth>
          <DialogTitle>Session Timeout</DialogTitle>
          <DialogContent>
            <Typography>
              Your session is about to expire. Do you want to stay signed in?
            </Typography>
          </DialogContent>
          <DialogActions>
            <CommonButton
              text="Sign out now"
              size="large"
              handleClick={handleManualLogout}
              hoverColor="#FF5A01"
            />
            <CommonButton
              text="Stay signed in"
              size="large"
              handleClick={handleStay}
              hoverColor="#FF5A01"
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
